// Bookmark card (link preview) shared model + markdown helper. A bookmark round-trips to a
// `:::bookmark` directive whose body is a single JSON object (see markdown/parse.ts + serialize.ts).
// Kept here so the editor (insert-on-paste) and the server (share enrichment) emit identical markdown.

export interface BookmarkMeta {
  /** The canonical link URL (required). */
  url: string;
  /** Page title (og:title / <title>), or '' when unresolved. */
  title: string;
  /** Short description (og:description / meta description), or ''. */
  description: string;
  /** Thumbnail — a stored `lapo://` ref (re-hosted og:image) or an https URL; '' when none. */
  image: string;
  /** Favicon https URL, or ''. */
  favicon: string;
}

/** Coerce a partial/untrusted object into a full BookmarkMeta, dropping non-strings. */
export function coerceBookmarkMeta(raw: Partial<BookmarkMeta> | null | undefined): BookmarkMeta | null {
  if (!raw || typeof raw.url !== 'string' || !raw.url) return null;
  const str = (v: unknown): string => (typeof v === 'string' ? v : '');
  return {
    url: raw.url,
    title: str(raw.title),
    description: str(raw.description),
    image: str(raw.image),
    favicon: str(raw.favicon),
  };
}

/** The `:::bookmark` directive markdown for a resolved link (one JSON body line). */
export function renderBookmarkMarkdown(meta: BookmarkMeta): string {
  const body = {
    url: meta.url,
    title: meta.title ?? '',
    description: meta.description ?? '',
    image: meta.image ?? '',
    favicon: meta.favicon ?? '',
  };
  return [':::bookmark', JSON.stringify(body), ':::'].join('\n');
}
